import { useState, useEffect } from 'react';

type UseKeyboardDetectionOptions = Readonly<{
  lockScroll?: boolean;
  isMobile?: boolean;
}>;

export const useKeyboardDetection = (options: UseKeyboardDetectionOptions = {}) => {
  const { lockScroll = false, isMobile = false } = options;
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (!window.visualViewport) {
      return;
    }

    const visualViewport = window.visualViewport;

    const handleViewportChange = () => {
      const heightDifference = Math.max(0, window.innerHeight - visualViewport.height);

      setKeyboardHeight(heightDifference);
    };

    visualViewport.addEventListener('resize', handleViewportChange);

    handleViewportChange();

    return () => visualViewport.removeEventListener('resize', handleViewportChange);
  }, []);

  useEffect(() => {
    if (!lockScroll || !isMobile) return;

    // save current scroll position and lock body
    const scrollY = window.scrollY;

    window.scrollTo(0, 0);

    // lock body
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    // prevent touch scrolling except in scrollable children
    const preventTouchMove = (e: TouchEvent) => {
      let target = e.target as HTMLElement;

      while (target && target !== document.body) {
        const style = window.getComputedStyle(target);

        // allow scrolling in scrollable elements
        if (
          style.overflow === 'auto' ||
          style.overflow === 'scroll' ||
          style.overflowY === 'auto' ||
          style.overflowY === 'scroll'
        ) {
          return;
        }

        target = target.parentElement as HTMLElement;
      }

      e.preventDefault();
    };

    // force viewport to stay at top
    const preventScroll = () => {
      window.scrollTo(0, 0);
    };

    document.addEventListener('touchmove', preventTouchMove, { passive: false });
    window.addEventListener('scroll', preventScroll);

    return () => {
      // restore everything
      const bodyTop = document.body.style.top;

      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.body.style.touchAction = '';

      if (bodyTop) {
        window.scrollTo(0, parseInt(bodyTop || '0', 10) * -1);
      }

      document.removeEventListener('touchmove', preventTouchMove);
      window.removeEventListener('scroll', preventScroll);
    };
  }, [lockScroll, isMobile, keyboardHeight]);

  return {
    keyboardHeight,
    isKeyboardVisible: keyboardHeight > 0,
  };
};
